// src/modules/ida/components/DocumentPreview.tsx
//
// What? Slide-in modal for previewing source documents.
// Why? To show users the context of citations without leaving the chat.
// How? Uses MUI Joy Drawer sliding from right with document content.

import React, { useEffect, useState, useCallback } from "react";
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Stack,
  Divider,
  CircularProgress,
  Chip,
  Sheet,
  Button,
  Link,
  Switch,
  FormControl,
  FormLabel,
  Alert,
  Tooltip,
  Input,
} from "@mui/joy";
import CloseIcon from "@mui/icons-material/Close";
import DescriptionIcon from "@mui/icons-material/Description";
import LanguageIcon from "@mui/icons-material/Language";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import LockIcon from "@mui/icons-material/Lock";
import PublicIcon from "@mui/icons-material/Public";
import BusinessIcon from "@mui/icons-material/Business";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { useAuthFetch } from "../../../utils/useAuthFetch";
import {
  useGetDocumentAccess,
  useUpdateDocumentPublish,
  useUpdateDocumentPublic,
  type DocumentAccessInfo,
} from "../services/api";
import { buildPublishedDocumentUrl } from "../utils/publicLinks";
import type { Citation } from "@metalmind-ab/mmagent-chat-ui";
import { getCitationDocumentId } from "../utils/citationDocument";

interface DocumentPreviewProps {
  open: boolean;
  onClose: () => void;
  workspaceId: string;
  citation: Citation | null;
}

interface DocumentData {
  id: string;
  filename: string;
  summary?: string;
  full_text?: string;
  page_count?: number;
  token_count: number;
  source_type: string;
}

// Helper to detect if a citation is from web search
const isWebSearchCitation = (citation: Citation | null): boolean => {
  if (!citation) return false;
  // Check by skill_name first (most reliable)
  if (citation.skill_name === "web_search") return true;
  // Fallback: check if id looks like a URL
  const id = citation.id || "";
  return id.startsWith("http://") || id.startsWith("https://");
};

// Extract domain from URL for display
const extractDomain = (url: string): string => {
  try {
    const urlObj = new URL(url);
    return urlObj.hostname;
  } catch {
    return url;
  }
};

// Life science skills that return external citations (not IDA documents)
const EXTERNAL_SOURCE_SKILLS = [
  "pubmed_search",
  "clinical_trials_search",
  "preprint_search",
  "chembl_search",
  "open_targets_search",
  "uniprot_search",
  "reactome_search",
  "openfda_adverse_events",
  "openfda_drug_labels",
  "openfda_approvals",
  "openfda_recalls",
  "dailymed_labels",
  "ema_medicines",
  "ema_product_info",
  "eudravigilance_search",
];

// Display names for life science skills
const SKILL_DISPLAY_NAMES: Record<string, string> = {
  pubmed_search: "PubMed",
  clinical_trials_search: "ClinicalTrials.gov",
  preprint_search: "bioRxiv/medRxiv",
  chembl_search: "ChEMBL",
  open_targets_search: "Open Targets",
  uniprot_search: "UniProt",
  reactome_search: "Reactome",
  openfda_adverse_events: "FDA FAERS",
  openfda_drug_labels: "FDA Drug Labels",
  openfda_approvals: "Drugs@FDA",
  openfda_recalls: "FDA Recalls",
  dailymed_labels: "DailyMed",
  ema_medicines: "EMA",
  ema_product_info: "EMA ePI",
  eudravigilance_search: "EudraVigilance",
};

// Helper to detect if a citation is from a life science external source
const isExternalSourceCitation = (citation: Citation | null): boolean => {
  if (!citation) return false;
  if (
    citation.skill_name &&
    EXTERNAL_SOURCE_SKILLS.includes(citation.skill_name)
  )
    return true;
  return false;
};

// Get external URL from citation extra fields (different skills use different field names)
const getExternalUrl = (citation: Citation | null): string | null => {
  if (!citation?.extra) return null;
  return (citation.extra.url ||
    citation.extra.ema_url ||
    citation.extra.report_url) as string | null;
};

// Get display name for the citation source
const getSourceDisplayName = (citation: Citation | null): string => {
  if (!citation?.skill_name) return "External Source";
  return SKILL_DISPLAY_NAMES[citation.skill_name] || citation.skill_name;
};

export const DocumentPreview: React.FC<DocumentPreviewProps> = ({
  open,
  onClose,
  workspaceId,
  citation,
}) => {
  const authFetch = useAuthFetch();
  const [document, setDocument] = useState<DocumentData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Publishing state
  const getDocumentAccess = useGetDocumentAccess();
  const updateDocumentPublish = useUpdateDocumentPublish();
  const updateDocumentPublic = useUpdateDocumentPublic();
  const [accessInfo, setAccessInfo] = useState<DocumentAccessInfo | null>(null);
  const [publishLoading, setPublishLoading] = useState(false);
  const [publishFeedback, setPublishFeedback] = useState<{
    type: "success" | "error";
    message: string;
  } | null>(null);
  const [copied, setCopied] = useState(false);

  // Check if this is a web search result
  const isWebSearch = isWebSearchCitation(citation);
  const webUrl = isWebSearch ? citation?.id || "" : "";

  // Check if this is an external source (life science skill)
  const isExternalSource = isExternalSourceCitation(citation);
  const externalUrl = getExternalUrl(citation);
  const sourceDisplayName = getSourceDisplayName(citation);

  // Extract doc_id from citation - it's in the 'id' field or 'extra.doc_id'
  const docId = getCitationDocumentId(citation);

  const loadDocument = useCallback(async () => {
    if (!docId) {
      console.warn("No doc_id in citation:", citation);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const url = `/api/workspace/ida/documents/${encodeURIComponent(docId)}?workspace_id=${workspaceId}&include_text=true`;
      console.log("Fetching document:", { docId, url, workspaceId });

      const data = await authFetch(url, "fetch document for preview");
      console.log("Document loaded:", data);
      setDocument(data);
    } catch (err) {
      console.error("Failed to load document:", err, { citation, workspaceId });
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }, [authFetch, docId, workspaceId, citation]);

  useEffect(() => {
    // Skip document loading for web search and external source citations
    // These don't have stored documents in IDA - they reference external sources
    if (open && docId && workspaceId && !isWebSearch && !isExternalSource) {
      loadDocument();
    }
  }, [open, docId, workspaceId, loadDocument, isWebSearch, isExternalSource]);

  // Load publish/access state for internal documents
  const loadAccessInfo = useCallback(async () => {
    if (!docId || !workspaceId) return;
    try {
      const info = await getDocumentAccess(workspaceId, docId);
      setAccessInfo(info);
    } catch {
      // Non-fatal: publish toggles won't show if access check fails
      setAccessInfo(null);
    }
  }, [getDocumentAccess, docId, workspaceId]);

  useEffect(() => {
    if (open && docId && workspaceId && !isWebSearch && !isExternalSource) {
      loadAccessInfo();
    }
    if (!open) {
      setAccessInfo(null);
      setPublishFeedback(null);
      setCopied(false);
    }
  }, [open, docId, workspaceId, loadAccessInfo, isWebSearch, isExternalSource]);

  // Handle publish toggle
  const handlePublishToggle = useCallback(
    async (checked: boolean) => {
      if (!docId || !workspaceId) return;
      setPublishLoading(true);
      setPublishFeedback(null);
      try {
        const info = await updateDocumentPublish(workspaceId, docId, checked);
        setAccessInfo(info);
        setPublishFeedback({
          type: "success",
          message: checked
            ? "Document published to organization"
            : "Document set to private",
        });
      } catch {
        setPublishFeedback({
          type: "error",
          message: "Failed to update publish state",
        });
      } finally {
        setPublishLoading(false);
        setTimeout(() => setPublishFeedback(null), 3000);
      }
    },
    [docId, workspaceId, updateDocumentPublish],
  );

  // Handle public toggle
  const handlePublicToggle = useCallback(
    async (checked: boolean) => {
      if (!docId || !workspaceId) return;
      setPublishLoading(true);
      setPublishFeedback(null);
      try {
        const info = await updateDocumentPublic(workspaceId, docId, checked);
        setAccessInfo(info);
        setPublishFeedback({
          type: "success",
          message: checked
            ? "Document is now publicly accessible"
            : "Public access revoked",
        });
      } catch {
        setPublishFeedback({
          type: "error",
          message: "Failed to update public state",
        });
      } finally {
        setPublishLoading(false);
        setTimeout(() => setPublishFeedback(null), 3000);
      }
    },
    [docId, workspaceId, updateDocumentPublic],
  );

  // Build the stable canonical published URL (latest published version in the chain).
  const publicUrl =
    accessInfo?.is_published
      ? buildPublishedDocumentUrl(window.location.origin, accessInfo.public_doc_id)
      : null;

  // Copy public URL to clipboard
  const handleCopyPublicUrl = useCallback(async () => {
    if (!publicUrl) return;
    try {
      await navigator.clipboard.writeText(publicUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback for non-secure contexts
      const textArea = globalThis.document.createElement("textarea");
      textArea.value = publicUrl;
      globalThis.document.body.appendChild(textArea);
      textArea.select();
      globalThis.document.execCommand("copy");
      globalThis.document.body.removeChild(textArea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  }, [publicUrl]);

  // Get access level display info
  const getAccessLevelDisplay = () => {
    if (!accessInfo) return null;
    switch (accessInfo.access_level) {
      case "published_public":
        return {
          label: "Published (Public)",
          color: "success" as const,
          icon: <PublicIcon sx={{ fontSize: 16 }} />,
        };
      case "published_org":
        return {
          label: "Published (Org)",
          color: "primary" as const,
          icon: <BusinessIcon sx={{ fontSize: 16 }} />,
        };
      default:
        return {
          label: "Private",
          color: "neutral" as const,
          icon: <LockIcon sx={{ fontSize: 16 }} />,
        };
    }
  };

  // Render the document content with markdown formatting
  const renderDocumentContent = () => {
    if (!document?.full_text) {
      return (
        <Typography
          level="body-sm"
          sx={{ color: "text.tertiary", fontStyle: "italic" }}
        >
          Full text not available for this document.
        </Typography>
      );
    }

    return (
      <Box
        sx={{
          fontSize: "0.875rem",
          lineHeight: 1.8,
          "& p": { my: 1 },
          "& h1": { mt: 2, mb: 1, fontSize: "1.25rem", fontWeight: 600 },
          "& h2": { mt: 2, mb: 1, fontSize: "1.1rem", fontWeight: 600 },
          "& h3": { mt: 1.5, mb: 0.5, fontSize: "1rem", fontWeight: 600 },
          "& ul, & ol": { pl: 2, my: 1 },
          "& li": { mb: 0.5 },
          "& code": {
            bgcolor: "neutral.100",
            px: 0.5,
            py: 0.25,
            borderRadius: "sm",
            fontSize: "0.85em",
          },
          "& pre": {
            bgcolor: "neutral.100",
            p: 1.5,
            borderRadius: "sm",
            overflow: "auto",
            "& code": { bgcolor: "transparent", p: 0 },
          },
          "& blockquote": {
            borderLeft: "3px solid",
            borderColor: "neutral.300",
            pl: 2,
            ml: 0,
            fontStyle: "italic",
            color: "text.secondary",
          },
        }}
      >
        <ReactMarkdown remarkPlugins={[remarkGfm]}>
          {document.full_text}
        </ReactMarkdown>
      </Box>
    );
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      anchor="right"
      size="lg"
      slotProps={{
        content: {
          sx: {
            width: { xs: "100%", sm: "600px", md: "700px" },
            maxWidth: "100vw",
          },
        },
      }}
    >
      <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
        {/* Header */}
        <Box
          sx={{
            p: 2,
            borderBottom: 1,
            borderColor: "divider",
            bgcolor: "background.surface",
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
          >
            <Stack direction="row" alignItems="center" spacing={1.5}>
              {isWebSearch ? (
                <LanguageIcon sx={{ color: "success.500" }} />
              ) : isExternalSource ? (
                <LanguageIcon sx={{ color: "primary.500" }} />
              ) : (
                <DescriptionIcon sx={{ color: "primary.500" }} />
              )}
              <Box>
                <>
                  <Typography level="title-md">
                    {isWebSearch
                      ? citation?.source || "Web Search Result"
                      : isExternalSource
                        ? citation?.source || sourceDisplayName
                        : document?.filename ||
                          citation?.source ||
                          "Document Preview"}
                  </Typography>
                  {isWebSearch && webUrl && (
                    <Typography level="body-xs" sx={{ color: "text.tertiary" }}>
                      {extractDomain(webUrl)}
                    </Typography>
                  )}
                  {isExternalSource && (
                    <Typography level="body-xs" sx={{ color: "text.tertiary" }}>
                      {sourceDisplayName}
                    </Typography>
                  )}
                  {!isWebSearch &&
                    !isExternalSource &&
                    citation?.extra?.page && (
                      <Typography
                        level="body-xs"
                        sx={{ color: "text.tertiary" }}
                      >
                        Page {citation.extra.page as number}
                      </Typography>
                    )}
                </>
              </Box>
            </Stack>
            <IconButton variant="plain" color="neutral" onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Stack>
        </Box>

        {/* Publishing controls - only for internal documents */}
        {!isWebSearch && !isExternalSource && accessInfo && (
          <Box
            sx={{
              px: 2,
              py: 1.5,
              borderBottom: 1,
              borderColor: "divider",
              bgcolor: "background.level1",
            }}
          >
            {/* Access level indicator */}
            {(() => {
              const display = getAccessLevelDisplay();
              return display ? (
                <Chip
                  size="sm"
                  variant="soft"
                  color={display.color}
                  startDecorator={display.icon}
                  sx={{ mb: 1.5 }}
                >
                  {display.label}
                </Chip>
              ) : null;
            })()}

            {/* Toggle switches - only for editors */}
            {accessInfo.can_publish && (
              <Stack direction="row" spacing={3} alignItems="center">
                <FormControl orientation="horizontal" sx={{ gap: 1 }}>
                  <FormLabel sx={{ fontSize: "0.8rem" }}>Publish</FormLabel>
                  <Switch
                    size="sm"
                    checked={accessInfo.is_published}
                    onChange={(e) => handlePublishToggle(e.target.checked)}
                    disabled={publishLoading}
                    color={accessInfo.is_published ? "primary" : "neutral"}
                  />
                </FormControl>

                {accessInfo.is_published && (
                  <FormControl orientation="horizontal" sx={{ gap: 1 }}>
                    <FormLabel sx={{ fontSize: "0.8rem" }}>Public</FormLabel>
                    <Switch
                      size="sm"
                      checked={accessInfo.is_public}
                      onChange={(e) => handlePublicToggle(e.target.checked)}
                      disabled={publishLoading || !accessInfo.is_published}
                      color={accessInfo.is_public ? "success" : "neutral"}
                    />
                  </FormControl>
                )}

                {publishLoading && <CircularProgress size="sm" />}
              </Stack>
            )}

            {/* Published URL with copy button */}
            {accessInfo.is_published && publicUrl && (
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                sx={{ mt: 1.5 }}
              >
                <Input
                  size="sm"
                  readOnly
                  value={publicUrl}
                  sx={{ flex: 1, fontSize: "0.75rem" }}
                />
                <Tooltip title={copied ? "Copied!" : "Copy link"}>
                  <IconButton
                    size="sm"
                    variant="soft"
                    color={copied ? "success" : "neutral"}
                    onClick={handleCopyPublicUrl}
                  >
                    <ContentCopyIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
              </Stack>
            )}

            {/* Feedback message */}
            {publishFeedback && (
              <Alert
                size="sm"
                color={
                  publishFeedback.type === "success" ? "success" : "danger"
                }
                variant="soft"
                sx={{ mt: 1 }}
              >
                {publishFeedback.message}
              </Alert>
            )}
          </Box>
        )}

        {/* Citation quote - hide for web/external sources (shown in their content area) */}
        {!isWebSearch &&
          !isExternalSource &&
          citation?.snippet &&
          (() => {
            const snippetText = citation.snippet.slice(0, 100);
            const summaryText = document?.summary?.slice(0, 100) || "";
            const isDuplicate =
              summaryText &&
              (summaryText.includes(snippetText) ||
                snippetText.includes(summaryText));
            if (isDuplicate) return null;
            return (
              <Sheet
                variant="soft"
                color="primary"
                sx={{
                  mx: 2,
                  mt: 2,
                  p: 2,
                  borderRadius: "md",
                  borderLeft: 4,
                  borderColor: "primary.500",
                }}
              >
                <Typography
                  level="body-sm"
                  sx={{ fontStyle: "italic", whiteSpace: "pre-wrap" }}
                >
                  "{citation.snippet}"
                </Typography>
              </Sheet>
            );
          })()}

        {/* Content area */}
        <Box sx={{ flex: 1, overflow: "auto", p: 2 }}>
          {/* Web Search Result View */}
          {isWebSearch && (
            <Box>
              <>
                {/* Open in New Tab Button - Prominent at top */}
                <Button
                  component="a"
                  href={webUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="solid"
                  color="primary"
                  size="lg"
                  fullWidth
                  endDecorator={<OpenInNewIcon />}
                  sx={{ mb: 3 }}
                >
                  Open Page in New Tab
                </Button>

                {/* URL display */}
                <Box sx={{ mb: 3 }}>
                  <Typography level="title-sm" sx={{ mb: 0.5 }}>
                    Source URL
                  </Typography>
                  <Link
                    href={webUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    level="body-sm"
                    sx={{
                      wordBreak: "break-all",
                      color: "primary.600",
                    }}
                  >
                    {webUrl}
                  </Link>
                </Box>

                <Divider sx={{ my: 2 }} />

                {/* Full Page Content (if fetched) */}
                {citation?.extra?.content && (
                  <Box sx={{ mb: 3 }}>
                    <Typography level="title-sm" sx={{ mb: 1 }}>
                      Page Content
                    </Typography>
                    <Sheet
                      variant="outlined"
                      sx={{
                        p: 2,
                        borderRadius: "md",
                        maxHeight: "400px",
                        overflow: "auto",
                      }}
                    >
                      <Box
                        sx={{
                          fontSize: "0.875rem",
                          lineHeight: 1.8,
                          "& p": { my: 1 },
                          "& h2": {
                            mt: 2,
                            mb: 1,
                            fontSize: "1.1rem",
                            fontWeight: 600,
                          },
                          "& h3": {
                            mt: 1.5,
                            mb: 0.5,
                            fontSize: "1rem",
                            fontWeight: 600,
                          },
                          "& ul, & ol": { pl: 2, my: 1 },
                          "& li": { mb: 0.5 },
                        }}
                      >
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>
                          {citation.extra.content as string}
                        </ReactMarkdown>
                      </Box>
                    </Sheet>
                  </Box>
                )}

                {/* Search Result Snippet - only show if no full content */}
                {citation?.snippet && !citation?.extra?.content && (
                  <Box>
                    <Typography level="title-sm" sx={{ mb: 1 }}>
                      Search Result Excerpt
                    </Typography>
                    <Sheet
                      variant="soft"
                      color="neutral"
                      sx={{
                        p: 2,
                        borderRadius: "md",
                        borderLeft: 4,
                        borderColor: "neutral.400",
                      }}
                    >
                      <Typography
                        level="body-md"
                        sx={{
                          lineHeight: 1.7,
                          whiteSpace: "pre-wrap",
                        }}
                      >
                        {citation.snippet}
                      </Typography>
                    </Sheet>
                  </Box>
                )}

                {/* Metadata chips */}
                <Stack direction="row" spacing={1} sx={{ mt: 3 }}>
                  <>
                    <Chip size="sm" variant="soft" color="success">
                      Web Search Result
                    </Chip>
                    {citation?.extra?.content && (
                      <Chip size="sm" variant="soft" color="primary">
                        Full Content
                      </Chip>
                    )}
                    <Chip size="sm" variant="soft" color="neutral">
                      {extractDomain(webUrl)}
                    </Chip>
                  </>
                </Stack>
              </>
            </Box>
          )}

          {/* External Source View (life science results) */}
          {isExternalSource && (
            <Box>
              {/* Open in Source Button - only if URL available */}
              {externalUrl && (
                <Button
                  component="a"
                  href={externalUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="solid"
                  color="primary"
                  size="lg"
                  fullWidth
                  endDecorator={<OpenInNewIcon />}
                  sx={{ mb: 3 }}
                >
                  Open in {sourceDisplayName}
                </Button>
              )}

              {/* Source URL display - only if URL available */}
              {externalUrl && (
                <Box sx={{ mb: 3 }}>
                  <Typography level="title-sm" sx={{ mb: 0.5 }}>
                    Source URL
                  </Typography>
                  <Link
                    href={externalUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    level="body-sm"
                    sx={{ wordBreak: "break-all", color: "primary.600" }}
                  >
                    {externalUrl}
                  </Link>
                </Box>
              )}

              {externalUrl && <Divider sx={{ my: 2 }} />}

              {/* Citation snippet */}
              {citation?.snippet && (
                <Box sx={{ mb: 3 }}>
                  <Typography level="title-sm" sx={{ mb: 1 }}>
                    Summary
                  </Typography>
                  <Sheet
                    variant="soft"
                    color="neutral"
                    sx={{
                      p: 2,
                      borderRadius: "md",
                      borderLeft: 4,
                      borderColor: "neutral.400",
                    }}
                  >
                    <Typography
                      level="body-md"
                      sx={{ lineHeight: 1.7, whiteSpace: "pre-wrap" }}
                    >
                      {citation.snippet}
                    </Typography>
                  </Sheet>
                </Box>
              )}

              {/* Metadata from extra fields */}
              {citation?.extra && Object.keys(citation.extra).length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Typography level="title-sm" sx={{ mb: 1 }}>
                    Details
                  </Typography>
                  <Stack spacing={1}>
                    {Object.entries(citation.extra)
                      .filter(
                        ([key, value]) =>
                          !["url", "ema_url", "report_url"].includes(key) &&
                          value !== null &&
                          value !== undefined &&
                          value !== "",
                      )
                      .slice(0, 8)
                      .map(([key, value]) => (
                        <Box key={key} sx={{ display: "flex", gap: 1 }}>
                          <Typography
                            level="body-sm"
                            sx={{
                              fontWeight: 600,
                              minWidth: 140,
                              flexShrink: 0,
                            }}
                          >
                            {key
                              .replace(/_/g, " ")
                              .replace(/\b\w/g, (l) => l.toUpperCase())}
                            :
                          </Typography>
                          <Typography
                            level="body-sm"
                            sx={{ color: "text.secondary" }}
                          >
                            {typeof value === "boolean"
                              ? value
                                ? "Yes"
                                : "No"
                              : String(value)}
                          </Typography>
                        </Box>
                      ))}
                  </Stack>
                </Box>
              )}

              {/* Source chip */}
              <Stack direction="row" spacing={1} sx={{ mt: 3 }}>
                <Chip size="sm" variant="soft" color="primary">
                  {sourceDisplayName}
                </Chip>
                {externalUrl && (
                  <Chip size="sm" variant="soft" color="success">
                    External Link
                  </Chip>
                )}
              </Stack>
            </Box>
          )}

          {/* Document View (non-web-search AND non-external-source) */}
          {!isWebSearch && !isExternalSource && (
            <>
              {loading && (
                <Stack
                  alignItems="center"
                  justifyContent="center"
                  sx={{ py: 8 }}
                >
                  <CircularProgress size="lg" />
                  <Typography
                    level="body-sm"
                    sx={{ mt: 2, color: "text.tertiary" }}
                  >
                    Loading document...
                  </Typography>
                </Stack>
              )}

              {error && (
                <Box
                  sx={{
                    p: 3,
                    bgcolor: "danger.50",
                    borderRadius: "md",
                    textAlign: "center",
                  }}
                >
                  <Typography level="body-sm" sx={{ color: "danger.700" }}>
                    Failed to load document: {error}
                  </Typography>
                </Box>
              )}

              {!loading && !error && document && (
                <Box>
                  {/* Document metadata */}
                  <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                    <Chip size="sm" variant="soft" color="neutral">
                      {document.source_type}
                    </Chip>
                    {document.page_count && (
                      <Chip size="sm" variant="soft" color="neutral">
                        {document.page_count} pages
                      </Chip>
                    )}
                    <Chip size="sm" variant="soft" color="neutral">
                      {document.token_count.toLocaleString()} tokens
                    </Chip>
                  </Stack>

                  {/* Summary */}
                  {document.summary && (
                    <Box sx={{ mb: 3 }}>
                      <Typography level="title-sm" sx={{ mb: 1 }}>
                        Summary
                      </Typography>
                      <Box
                        sx={{
                          color: "text.secondary",
                          fontSize: "0.875rem",
                          "& p": { m: 0, mb: 1 },
                          "& p:last-child": { mb: 0 },
                          "& ul, & ol": { pl: 2.5, my: 1 },
                          "& li": { mb: 0.5 },
                          "& h1, & h2, & h3, & h4": { mt: 1.5, mb: 0.5 },
                        }}
                      >
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>
                          {document.summary}
                        </ReactMarkdown>
                      </Box>
                    </Box>
                  )}

                  <Divider sx={{ my: 2 }} />

                  {/* Full text */}
                  <Typography level="title-sm" sx={{ mb: 1 }}>
                    Document Content
                  </Typography>
                  {renderDocumentContent()}
                </Box>
              )}
            </>
          )}
        </Box>
      </Box>
    </Drawer>
  );
};
